import { CdkOverlayOrigin } from '@angular/cdk/overlay'
import {
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'

import { GridApi, ValueGetterParams } from 'ag-grid-community'
import { MenuItem } from 'primeng/api'
import { Menu, MenuModule } from 'primeng/menu'
import { Table } from 'primeng/table'
import { TieredMenuModule } from 'primeng/tieredmenu'

import { EMPTY, Observable, catchError, filter, of, switchMap, take, tap } from 'rxjs'

import {
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalModule,
  FlxModalRef,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { FlxContentValueEditComponent } from '@flowx/designer/modules/cms/pages/content-value-edit/content-value-edit.component'
import { SubstitutionTagsStore } from '@flowx/designer/modules/cms/pages/substitution-tags/substitution-tags.store'
import { Stage } from '@flowx/designer/modules/core/dictionary/stage.dictionary'
import { SystemStore } from '@flowx/designer/modules/integration-designer/components/systems-page/systems.store'
import { AvailableConflictResourceTypes } from '@flowx/designer/modules/merge-conflicts/merge-conflicts.dictionary'
import {
  DefaultActionsColumn,
  FlxResourcesGridComponent,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxResourcesPageComponent } from '@flowx/designer/modules/projects/components/resources-page/flx-resources-page.component'
import {
  AuditDefaultMenuItem,
  DeleteDefaultMenuItem,
  DetailsDefaultMenuItem,
  ExportDefaultMenuItem,
  ExportDefaultSubMenuItem,
  ImportDefaultMenuItem,
  ImportDefaultSubMenuItem,
  ResourceHistoryDefaultMenuItem,
  compareMenuItemsByOrder,
} from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'
import { FlxMenuCloseOnClickOutsideDirective } from '@flowx/designer/modules/projects/directives/menu-click-outside/menu-click-outside.directive'
import { FlxMenuOnShowAlignmentDirective } from '@flowx/designer/modules/projects/directives/menu-on-show-alignment/menu-on-show-alignment.directive'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { DetailedPermissions } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { AuditLogsComponent } from '@flowx/designer/shared/components/audit-logs/audit-logs.component'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { ImportExportLoadingModalComponent } from '@flowx/designer/shared/components/modals/import-export-loading-modal/import-export-loading-modal.component'
import { FlxOverlayComponent } from '@flowx/designer/shared/components/overlay/overlay.component'
import { ResourceUsagesDeleteConfirmationComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages-delete-confirmation.component'
import { ResourceUsagesComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import {
  ContentValue,
  Enumeration,
  EnumerationToastMessages,
  prepareForTable,
} from '@flowx/designer/shared/entities/enumeration.entity'
import { isImportSuccessful } from '@flowx/designer/shared/entities/existing-resource.entity'
import {
  ResourceUsage,
  ResourceUsageDeleteTemplateData,
  buildResourceDeleteConfirmation,
} from '@flowx/designer/shared/model/resource-usage.model'
import { ComponentTypeEnum, ElementTypeEnum } from '@flowx/designer/shared/services/audit.service'
import { ImportExportService } from '@flowx/designer/shared/services/import-export.service'
import { ResourceHistoryModalService } from '@flowx/designer/shared/services/resource-history-modal.service'
import { ResourceUsagesService } from '@flowx/designer/shared/services/resource-usages.service'
import { RoutePersistenceService } from '@flowx/designer/shared/services/route-persistence.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { ExportResourceType, exportFileFormat } from '@flowx/designer/shared/utils/file.utils'
import { downloadBlob, loadLocalFile } from '@flowx/shared/utils'

@Component({
  selector: 'app-substitution-tags',
  imports: [
    FlxResourcesGridComponent,
    MenuModule,
    FlxButtonModule,
    FlxIconModule,
    FlxFormControlModule,
    ReactiveFormsModule,
    FlxOverlayComponent,
    CdkOverlayOrigin,
    AuditLogsComponent,
    FlxModalModule,
    FlxResourcesPageComponent,
    TieredMenuModule,
    FlxSpinnerModule,
    FlxMenuCloseOnClickOutsideDirective,
    FlxMenuOnShowAlignmentDirective,
    ResourceUsagesDeleteConfirmationComponent,
  ],
  templateUrl: './substitution-tags.component.html',
  styleUrls: ['./substitution-tags.component.scss'],
  providers: [SubstitutionTagsStore, SystemStore],
  host: { class: 'h-full' },
})
export class SubstitutionTagsComponent {
  #destroyRef = inject(DestroyRef)
  #substitutionTagStore = inject(SubstitutionTagsStore)
  #toastMessageService = inject(ToastMessageService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #flxModalService = inject(FlxModalService)
  #importExportService = inject(ImportExportService)
  #systemStore = inject(SystemStore)
  #resourceUsagesService = inject(ResourceUsagesService)
  #route = inject(ActivatedRoute)
  #resourceHistoryModalService = inject(ResourceHistoryModalService)
  #routePersistenceService = inject(RoutePersistenceService)

  private readonly table = viewChild<Table>('dt1')

  // TEMPLATES
  private readonly resourceUsagesDeleteTRef =
    viewChild<TemplateRef<ResourceUsageDeleteTemplateData>>('resourceUsagesDelete')

  protected readonly actionsMenu = viewChild<Menu>('actionsMenuElement')

  // TEMPLATE IMPORTS
  protected readonly ComponentTypeEnum = ComponentTypeEnum
  protected readonly ElementTypeEnum = ElementTypeEnum

  // PERMISSIONS
  accessRights = input<DetailedPermissions>()

  // STORES
  loading = computed(() => {
    const storeLoading = this.#substitutionTagStore.loading()
    const hasData = this.#substitutionTagStore.substitutionTagsEnumeration()
    const hasSettings = this.#applicationDetailsStore.applicationSettings()?.appVersionSettings
    // Show loading if store is loading OR if we have settings but no data yet
    return storeLoading || (hasSettings && !hasData)
  })
  readOnly = this.#applicationDetailsStore.isVersionCommitted
  appVersionId = this.#applicationDetailsStore.appVersionId
  languages = this.#applicationDetailsStore.languages
  sourceSystems = this.#systemStore.systems
  readonly ApplicationResourceType = ApplicationResourceType

  sourceSystemsLoaded = toObservable(this.#systemStore.loaded).pipe(
    filter((value: boolean) => value)
  )

  substitutionTagsEnumeration = computed(() => {
    const substitutionTagsEnumeration = this.#substitutionTagStore.substitutionTagsEnumeration()
    return prepareForTable(substitutionTagsEnumeration)
  })

  columns = signal<
    {
      header: string
      field: string
      size: string
    }[]
  >([])

  substitutionTagsTableDef = computed(() => {
    const actions = [
      {
        icon: 'ph-dots-three-vertical',
        tooltip: '',
        dataTestId: 'row-menu-button',
        clickAction: (substitutionTag: ContentValue, event: Event) =>
          this.openMenuByContext(event, substitutionTag),
      },
    ]

    return [
      {
        headerName: 'Key',
        field: 'code',
        minWidth: 75,
      },
      ...this.languages().map((item) => ({
        headerName: item.code,
        field: item.code,
        minWidth: 45,
        valueGetter: (params: ValueGetterParams) => params.data[item.code] ?? '-',
      })),
      {
        ...DefaultActionsColumn,
        width: getActionsColumnLength(actions.length),
        cellRenderer: ActionsCellComponent<Stage>,
        cellRendererParams: {
          actions,
        },
      },
    ]
  })

  gridApi: GridApi

  showAuditLogsOverlay = signal(false)
  selectedSubstitutionTag: WritableSignal<ContentValue | null> = signal(null)

  searchQuery = signal<string>(this.#route.snapshot.queryParams?.searchQuery ?? '')
  context = computed<{ filteredSubstitutionTags: ContentValue[] }>(() => {
    const searchQuery = this.searchQuery()
    const substitutionTags = this.substitutionTagsEnumeration().contentValues

    return {
      filteredSubstitutionTags:
        substitutionTags?.filter(
          (tag: ContentValue) =>
            !searchQuery ||
            (searchQuery && tag.code.toLowerCase().includes(searchQuery.toLowerCase())) ||
            Object.values(tag.contentMap).some((content: string) =>
              content?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        ) || [],
    }
  })

  // MENUS
  importExportMenuItems: Signal<MenuItem[]> = computed(() => {
    const readOnly = this.readOnly()
    const accessRights = this.accessRights()

    return [
      {
        ...ImportDefaultMenuItem(readOnly, accessRights.canImport),
        items: [
          {
            ...ImportDefaultSubMenuItem(false, true, 'ZIP'),
            command: () => this.importFromZip(),
          },
          {
            ...ImportDefaultSubMenuItem(false, true, 'CSV'),
            command: () => this.importCsv(),
          },
        ],
      },
      {
        ...ExportDefaultMenuItem(false, true, 'All'),
        items: [
          {
            ...ExportDefaultSubMenuItem(false, true, 'ZIP'),
            command: () => this.exportToZip(),
          },
          {
            ...ExportDefaultSubMenuItem(false, true, 'CSV'),
            command: () => this.exportCsv(),
          },
        ],
      },
      {
        ...ResourceHistoryDefaultMenuItem(),
        command: (): void => this.#openResourceHistoryModal(this.substitutionTagsEnumeration()),
      },
    ]
  })

  contextMenuItems: Signal<MenuItem[]> = computed(() => {
    const readOnly = this.readOnly()
    const selectedSubstitutionTag = this.selectedSubstitutionTag()
    const accessRights = this.accessRights()
    const canEditDetails = accessRights.canEdit && !readOnly
    return [
      {
        ...DetailsDefaultMenuItem(false, accessRights.canRead, !canEditDetails),
        command: (): void =>
          this.openContentValueEditModal(selectedSubstitutionTag, !canEditDetails),
      },
      {
        ...AuditDefaultMenuItem(false, true),
        command: () => this.showAuditLogsOverlay.set(true),
      },
      {
        ...DeleteDefaultMenuItem(
          readOnly,
          accessRights.canDelete && !selectedSubstitutionTag?.systemResource
        ),
        command: (): void => this.deleteSubstitutionTag(selectedSubstitutionTag),
      },
    ].sort(compareMenuItemsByOrder)
  })

  constructor() {
    effect(() => {
      const applicationVersionSettings =
        this.#applicationDetailsStore.applicationSettings()?.appVersionSettings
      if (applicationVersionSettings && !this.#substitutionTagStore.substitutionTagsEnumeration()) {
        // Only fetch if we haven't already loaded the data
        this.#substitutionTagStore.getAllSubstitutionTags()
      }
    })

    effect(() => {
      const languages = this.languages()
      if (languages && languages.length > 0) {
        this.columns.set([
          { header: 'Key', field: 'code', size: '0 0 230px' },
          ...languages.map((language) => ({
            header: language.code,
            field: language.code,
            size: '1 0 1',
          })),
          { header: '', field: 'actions', size: '0 0 140px' },
        ])
      }
    })
    effect(() => {
      this.table()?.filterGlobal(this.#route.snapshot.queryParams.searchQuery || '', 'contains')
    })

    this.#routePersistenceService.refreshResources$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#substitutionTagStore.getAllSubstitutionTags())
  }

  openContentValueEditModal(
    toBeUpdatedContentValue?: ContentValue,
    readOnly: boolean = false
  ): void {
    this.#flxModalService
      .show(
        FlxContentValueEditComponent,
        {
          closeOnBackdropClick: false,
          panelClass: 'max-h-[90%]',
        },
        {
          languages: this.languages(),
          contentValue: toBeUpdatedContentValue,
          readOnly,
          type: 'substitutionTags',
          isNew: !toBeUpdatedContentValue,
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((data) => {
        if (data) {
          this.#createUpdateSubstitutionTagValue(data, toBeUpdatedContentValue)
        }
      })
  }

  deleteSubstitutionTag(substitutionTagValue: ContentValue): void {
    of([])
      .pipe(
        switchMap((resourceUsages) =>
          this.#flxModalService
            .show(
              ConfirmationModalComponent,
              {
                closeOnBackdropClick: false,
                backdropClass: 'overlay-backdrop',
              },
              {
                ...buildResourceDeleteConfirmation(
                  substitutionTagValue.code,
                  ApplicationResourceType.Substitutiontag,
                  resourceUsages.length > 0
                ),
                resourceUsages,
                customTemplate: this.resourceUsagesDeleteTRef(),
                btnOkText: 'Delete',
              }
            )
            .data$.pipe(
              filter((data) => data.confirm),
              switchMap(() => this.#deleteContentValue(substitutionTagValue.valueId))
            )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  importFromZip(): void {
    this.#importExportService
      .importZipWithExistingResourcesCheck(ApplicationResourceType.Substitutiontag)
      .pipe(
        take(1),
        catchError(() => EMPTY),
        filter(isImportSuccessful),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.#substitutionTagStore.getAllSubstitutionTags()
      })
  }

  exportToZip(resourceId?: string): void {
    this.#importExportService
      .exportToZip(ApplicationResourceType.Substitutiontag, resourceId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((enumerationsBlob) => {
        downloadBlob(enumerationsBlob, exportFileFormat(ExportResourceType.SubstitutionTag))
        this.#toastMessageService.showSuccessMessage('Substitution Tags exported successfully')
      })
  }

  importCsv(): void {
    let modal: FlxModalRef

    loadLocalFile({ accept: ['.csv'] })
      .pipe(
        switchMap((files: FileList) => {
          modal = this.openImportExportModal('Import')

          return this.#importExportService.importCsv(
            ApplicationResourceType.Substitutiontag,
            files[0]
          )
        }),
        tap(() => {
          modal.close()
          this.#substitutionTagStore.getAllSubstitutionTags()
        }),
        catchError((error) => {
          modal.close()
          const parsedError = error.error?.defaultMessage ? ': ' + error.error?.defaultMessage : '.'

          this.#toastMessageService.showErrorMessage(
            `${EnumerationToastMessages.IMPORT_SUBSTITUTION_TAGS_FAILURE}${parsedError}`
          )

          return EMPTY
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() =>
        this.#toastMessageService.showSuccessMessage(
          EnumerationToastMessages.IMPORT_SUBSTITUTION_TAGS_SUCCESS
        )
      )
  }

  exportCsv(): void {
    const modal = this.openImportExportModal('Export')
    this.#systemStore.loadAll()

    this.sourceSystemsLoaded
      .pipe(
        switchMap(() => {
          const sourceSystemCodes = this.sourceSystems().map((system) => system.code)
          const languageCodes = this.languages().map((language) => language.code)

          return this.#importExportService
            .exportCsv(ApplicationResourceType.Substitutiontag, languageCodes, sourceSystemCodes)
            .pipe(
              tap(() => modal.close()),
              catchError((error) => {
                modal.close()

                const parsedError = error.error?.defaultMessage
                  ? `: ${error.error?.defaultMessage}`
                  : '.'

                this.#toastMessageService.showErrorMessage(
                  `${EnumerationToastMessages.EXPORT_SUBSTITUTION_TAGS_FAILURE}${parsedError}`
                )

                return EMPTY
              })
            )
        }),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((substitutionTagsBlob) => {
        downloadBlob(
          substitutionTagsBlob,
          `${exportFileFormat(ExportResourceType.SubstitutionTag)}.csv`
        )

        this.#toastMessageService.showSuccessMessage(
          EnumerationToastMessages.EXPORT_SUBSTITUTION_TAGS_SUCCESS
        )
      })
  }

  openImportExportModal(type: 'Import' | 'Export'): FlxModalRef {
    return this.#flxModalService.show(
      ImportExportLoadingModalComponent,
      {
        closeOnBackdropClick: false,
        backdropClass: 'overlay-backdrop',
      },
      {
        type,
      }
    )
  }

  openMenuByContext(event: Event, substitutionTag?: ContentValue): void {
    if (substitutionTag) {
      this.selectedSubstitutionTag.set(substitutionTag)
    }

    if (this.actionsMenu().visible) {
      this.actionsMenu().hide()
    } else {
      this.actionsMenu().model = this.contextMenuItems()
      this.actionsMenu().toggle(event)
    }
  }

  onSearchQueryChange(value: string): void {
    this.searchQuery.set(value)
  }

  openResourceUsagesModal(
    substitutionTag: ContentValue,
    source: 'rowAction' | 'pageAction' | 'fromDeleteConfirm' = 'pageAction',
    resourceUsages?: ResourceUsage[]
  ): void {
    let endpoint: Observable<ResourceUsage[]>

    if (!resourceUsages) {
      endpoint = this.#resourceUsagesService.getResourceUsages(substitutionTag.valueId)
    } else {
      endpoint = of(resourceUsages)
    }

    endpoint.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((resourceUsages) =>
      this.#flxModalService.show(
        ResourceUsagesComponent,
        { panelClass: 'w-[calc(100%-80px)] h-[calc(100%-80px)]' },
        {
          name: substitutionTag.code,
          resourceType: ApplicationResourceType.Substitutiontag,
          resourceUsages,
          source,
        }
      )
    )
  }

  #createUpdateSubstitutionTagValue(
    contentValue: ContentValue,
    toBeUpdatedContentValue?: ContentValue
  ): void {
    if (toBeUpdatedContentValue) {
      const originalValue = this.#substitutionTagStore
        .substitutionTagsEnumeration()
        .contentValues.find((value) => value.valueId === contentValue.valueId)

      if (originalValue.systemResource) {
        this.#updateContentValue({
          ...contentValue,
          code: originalValue.code,
          systemResource: originalValue.systemResource,
        })
      } else {
        this.#updateContentValue(contentValue)
      }
    } else {
      this.#createContentValue(contentValue)
    }
  }

  async #deleteContentValue(valueId: string): Promise<void> {
    try {
      await this.#substitutionTagStore.deleteSubstitutionTag(valueId)
      this.#toastMessageService.showSuccessMessage(`Substitution tag deleted`)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title ?? `Error deleting substitution tag`
      )
      console.error(error)
    }
  }

  async #updateContentValue(contentValue: Partial<ContentValue>): Promise<void> {
    try {
      await this.#substitutionTagStore.updateSubstitutionTags(contentValue)
      this.#toastMessageService.showSuccessMessage(`Substitution tag updated`)
      this.#flxModalService.hide()
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title ?? `Error updating substitution tag`
      )
      console.error(error)
    }
  }

  async #createContentValue(contentValue: Partial<ContentValue>): Promise<void> {
    try {
      await this.#substitutionTagStore.createSubstitutionTags(contentValue)
      this.#toastMessageService.showSuccessMessage(`Substitution tag created`)
      this.#flxModalService.hide()
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title ?? `Error creating substitution tag`
      )
      console.error(error)
    }
  }

  #openResourceHistoryModal(substitutionTag: Enumeration): void {
    this.#resourceHistoryModalService.toggleResourceHistoryModal(
      {
        resourceDefinitionId: substitutionTag.resourceDefinitionId,
        resourceType: AvailableConflictResourceTypes.SubstitutionTags,
        resourceName: substitutionTag.description.name,
      },
      'rowAction'
    )
  }
}
